import { useCallback, useEffect, useRef, useState } from "react";

export interface EditorCellFocusTarget {
    rowId: string;
    field: "name" | "code" | "account" | "type";
}

// Drawer open + virtualizer scrollToIndex both land after the first paints.
// 12 frames (~200ms) gave up before the name cell existed, so Edit scrolled
// the row into view and never entered the input.
const MAX_CELL_FOCUS_FRAMES = 60;

const findEditorCell = ({
    rowId,
    field,
}: EditorCellFocusTarget): Element | undefined => {
    const cellKey = `${rowId}:${field}`;
    // Nested display keys are JSON.stringify arrays, so they contain quotes a
    // CSS attribute selector cannot parse. Match the attribute by value instead.
    return Array.from(document.querySelectorAll("[data-editor-cell]")).find(
        (element) => element.getAttribute("data-editor-cell") === cellKey,
    );
};

// Base UI's hidden form input sits inside the anchor and bounces focus to
// the trigger, so it must not count as the cell's editable input.
const focusEditorCellInput = (cell: Element): boolean => {
    const input = cell.querySelector('input:not([aria-hidden="true"])');
    if (!(input instanceof HTMLInputElement)) {
        return false;
    }
    input.focus();
    input.select();
    return document.activeElement === input;
};

// A combobox cell keeps its input in a portal, so the open popup (whose input
// Base UI focuses itself) is the focused state; pressing again would close it.
const isEditorCellPopupOpen = (cell: Element): boolean =>
    cell.querySelector('button[aria-expanded="true"]') !== null;

// A trigger that stays closed after a press (disabled, read-only) still counts
// as focused so the retry loop does not keep pressing it.
const isEditorCellTriggerFocused = (cell: Element): boolean =>
    cell.querySelector("button[aria-expanded]") === document.activeElement;

// Base UI's Combobox trigger opens on mousedown (its click handler is replaced
// by the cell's own), so `click()` never opens it; replay a mouse press.
const pressPopupTrigger = (button: HTMLButtonElement): void => {
    button.focus();
    const pointerInit = {
        bubbles: true,
        cancelable: true,
        pointerType: "mouse",
        isPrimary: true,
    };
    button.dispatchEvent(new PointerEvent("pointerdown", pointerInit));
    button.dispatchEvent(new MouseEvent("mousedown", pointerInit));
    button.dispatchEvent(new MouseEvent("mouseup", pointerInit));
    button.dispatchEvent(new MouseEvent("click", pointerInit));
};

const pressEditorCellButton = (cell: Element): boolean => {
    const button = cell.querySelector("button");
    if (!button) {
        return false;
    }
    if (!button.hasAttribute("aria-expanded")) {
        button.click();
        return focusEditorCellInput(cell);
    }
    pressPopupTrigger(button);
    return isEditorCellPopupOpen(cell) || isEditorCellTriggerFocused(cell);
};

interface PendingFocus extends EditorCellFocusTarget {
    // Bumped on every request so re-targeting the same cell still re-triggers.
    requestId: number;
}

// Shared focus-retry machinery for the nested template editor. The
// memoized row components ignore table meta, so a prop-driven `autoFocus` would
// never reach an already-mounted row — driving the DOM directly works for both
// on-screen and freshly-mounted (scrolled-in / expanded) rows. `onBeforeFocus`
// (the nested view scrolls the row into view) runs on every retry frame: a row
// that is not in the model yet becomes reachable once the tree rebuilds, and
// re-scrolling keeps it in view while ViewTable is still expanding rows above
// it. Keep it free of state writes — see scrollNestedRowIntoView.
export const useEditorCellFocus = (
    onBeforeFocus?: (target: EditorCellFocusTarget) => void,
    // Called on every retry so a just-created row can resolve after the tree
    // rebuilds.
    resolveRowId?: (rowId: string) => string,
): { requestFocus: (target: EditorCellFocusTarget) => void } => {
    const [pendingFocus, setPendingFocus] = useState<PendingFocus | null>(null);
    const requestIdRef = useRef(0);
    const rafIdRef = useRef<number | null>(null);
    const onBeforeFocusRef = useRef(onBeforeFocus);
    onBeforeFocusRef.current = onBeforeFocus;
    const resolveRowIdRef = useRef(resolveRowId);
    resolveRowIdRef.current = resolveRowId;

    const requestFocus = useCallback((target: EditorCellFocusTarget) => {
        requestIdRef.current += 1;
        setPendingFocus({ ...target, requestId: requestIdRef.current });
    }, []);

    useEffect(() => {
        if (pendingFocus === null) {
            return;
        }
        const resolveTarget = (): EditorCellFocusTarget => ({
            rowId:
                resolveRowIdRef.current?.(pendingFocus.rowId) ??
                pendingFocus.rowId,
            field: pendingFocus.field,
        });
        let remainingFrames = MAX_CELL_FOCUS_FRAMES;
        let focusedFrames = 0;
        const attemptFocus = () => {
            const target = resolveTarget();
            onBeforeFocusRef.current?.(target);
            const cell = findEditorCell(target);
            const isFocused =
                cell !== undefined &&
                (focusEditorCellInput(cell) ||
                    isEditorCellPopupOpen(cell) ||
                    (focusedFrames > 0 && isEditorCellTriggerFocused(cell)));
            if (isFocused) {
                focusedFrames += 1;
                // Two frames: the drawer focus trap often steals focus once on
                // open, and the name field's onBlur would otherwise commit.
                if (focusedFrames >= 2) {
                    rafIdRef.current = null;
                    return;
                }
            } else {
                focusedFrames = 0;
                if (cell && pressEditorCellButton(cell)) {
                    focusedFrames = 1;
                }
            }
            remainingFrames -= 1;
            if (remainingFrames <= 0) {
                rafIdRef.current = null;
                return;
            }
            rafIdRef.current = requestAnimationFrame(attemptFocus);
        };
        rafIdRef.current = requestAnimationFrame(attemptFocus);
        return () => {
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };
    }, [pendingFocus]);

    return { requestFocus };
};
